import localizedPeriodChipRowSx from './localizedPeriodChipRowSx';
import { localize } from '../../i18n';
import DateInputCard from '../../components/ui/DateInputCard';
import SingleLineText from '../../components/ui/SingleLineText';
import React, { useLayoutEffect, useState } from 'react';
import { Box, Chip, Switch, TextField, Typography } from '@mui/material';

const DAY_PRESETS = [3, 5, 7];

function toDate(dateKey) {
    if (!dateKey) return null;
    const [year, month, day] = dateKey.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
}

function daysBetweenInclusive(startDate, endDate) {
    const start = toDate(startDate);
    const end = toDate(endDate);
    if (!start || !end || end < start) return 1;
    return Math.round((end - start) / 86400000) + 1;
}

function todayKey() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}


export function addDaysKey(dateKey, daysToAdd) {
    const base = toDate(dateKey) || new Date();
    const next = new Date(base);
    next.setDate(next.getDate() + daysToAdd);
    const year = next.getFullYear();
    const month = String(next.getMonth() + 1).padStart(2, '0');
    const day = String(next.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export default function MedicationPeriodEditor({
    value,
    onChange,
}) {
    const form = value || {};
    const startDate = form.startDate || todayKey();
    const indefinite = typeof form.indefinite === 'boolean'
        ? form.indefinite
        : !(form.endDate || form.durationDays);
    const durationDays = Number(form.durationDays) > 0 ? Number(form.durationDays) : 3;
    const isPresetDuration = !form.endDate && DAY_PRESETS.includes(durationDays);
    const customSelected = !indefinite && !isPresetDuration;
    const [focusCustomDuration, setFocusCustomDuration] = useState(false);
    const [customDurationOpen, setCustomDurationOpen] = useState(customSelected);
    const [customDurationDraft, setCustomDurationDraft] = useState(String(durationDays));

    useLayoutEffect(() => {
        setCustomDurationDraft(String(durationDays));
        if (indefinite) {
            setCustomDurationOpen(false);
            return;
        }
        if (customSelected) {
            setCustomDurationOpen(true);
        }
    }, [customSelected, durationDays, form.endDate, indefinite]);

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            minHeight: 52,
            borderRadius: 2.5,
            bgcolor: '#fff',
            fontWeight: 850,
        },
    };
    const chipSx = (selected) => ({
        height: 44,
        flex: '1 1 0',
        minWidth: 0,
        bgcolor: selected ? '#ffe566' : '#fff',
        color: selected ? '#5a4a00' : '#666',
        border: selected ? '1px solid #e6c800' : '1px solid #e5e7eb',
        fontSize: '0.82rem',
        fontWeight: 850,
        borderRadius: '999px',
        '& .MuiChip-label': {
            px: 0.65,
            overflow: 'visible',
            whiteSpace: 'nowrap',
        },
        '&:hover, &:focus, &.Mui-focusVisible': { bgcolor: selected ? '#ffe566' : '#fff' },
        '&:active': { bgcolor: selected ? '#f4d84d' : '#fff' },
    });
    const normalizeDurationInput = (rawValue) => rawValue.replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, '').slice(0, 3);
    const commitDurationDraft = (draft, fallback = durationDays) => {
        const next = Number(draft);
        if (!Number.isFinite(next) || next <= 0) return Number(fallback) > 0 ? Number(fallback) : 3;
        return Math.min(next, 365);
    };
    const endDateValue = form.endDate || addDaysKey(startDate, Math.max(durationDays - 1, 0));
    const handleStartDateChange = (event) => {
        const nextStartDate = event.target.value || startDate;
        if (indefinite) {
            onChange({ startDate: nextStartDate });
            return;
        }
        if (form.endDate) {
            const nextEndDate = toDate(form.endDate) && toDate(form.endDate) >= toDate(nextStartDate)
                ? form.endDate
                : nextStartDate;
            onChange({
                startDate: nextStartDate,
                endDate: nextEndDate,
                durationDays: daysBetweenInclusive(nextStartDate, nextEndDate),
                indefinite: false,
            });
            return;
        }
        onChange({
            startDate: nextStartDate,
            durationDays,
            indefinite: false,
        });
    };
    const toggleIndefinite = (checked) => {
        if (checked) {
            setCustomDurationOpen(false);
            onChange({ indefinite: true, durationDays: null, endDate: null });
            return;
        }
        onChange({
            indefinite: false,
            durationDays,
            endDate: form.endDate || null,
            startDate,
        });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.15 }}>
            <Box
                role="button"
                tabIndex={0}
                data-tappable="true"
                onClick={() => toggleIndefinite(!indefinite)}
                sx={{
                    minHeight: 38,
                    mb: 0.35,
                    display: 'inline-flex',
                    alignItems: 'center',
                    alignSelf: 'flex-start',
                    gap: 0.75,
                    cursor: 'pointer',
                }}
            >
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, color: '#1f1f1f', lineHeight: 1.25 }} noWrap>{localize("종료일 없이 계속 복용하기")}</Typography>
                <Switch
                    checked={indefinite}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => toggleIndefinite(event.target.checked)}
                    inputProps={{ 'aria-label': localize('종료일 없이 계속 복용하기') }}
                    sx={{
                        width: 46,
                        height: 30,
                        p: 0,
                        flexShrink: 0,
                        '& .MuiSwitch-switchBase': {
                            p: '3px',
                            '&.Mui-checked': {
                                transform: 'translateX(16px)',
                                color: '#fff',
                                '& + .MuiSwitch-track': {
                                    bgcolor: '#ffe566',
                                    borderColor: '#e6c800',
                                    opacity: 1,
                                },
                            },
                        },
                        '& .MuiSwitch-thumb': {
                            width: 24,
                            height: 24,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                        },
                        '& .MuiSwitch-track': {
                            borderRadius: 999,
                            bgcolor: '#e5e7eb',
                            border: '1px solid #d1d5db',
                            opacity: 1,
                        },
                    }}
                />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.65 }}>
                <DateInputCard
                    label={localize("시작일")} ariaLabel={localize("시작일 선택")}
                    minHeight={66}
                    value={startDate}
                    onChange={handleStartDateChange}
                    max="9999-12-31"
                />
                <DateInputCard
                    label={localize("종료일")} ariaLabel={localize("종료일 선택")}
                    minHeight={66}
                    disabled={indefinite}
                    value={endDateValue}
                    min={startDate}
                    max="9999-12-31"
                    onChange={(event) => {
                        const nextEndDate = event.target.value || endDateValue;
                        onChange({
                            endDate: nextEndDate,
                            durationDays: daysBetweenInclusive(startDate, nextEndDate),
                            indefinite: false,
                        });
                    }}
                />
            </Box>
            {!indefinite && (
                <>
                    <Box sx={{ display: 'flex', gap: 0.55, pb: 0.25, mt: 0.05, minWidth: 0, ...localizedPeriodChipRowSx() }}>
                        {DAY_PRESETS.map(day => {
                            const selected = !customDurationOpen && !form.endDate && durationDays === day;
                            return (
                                <Chip
                                    key={day}
                                    label={localize("{v0}일 동안", { v0: day })}
                                    onClick={() => {
                                        setCustomDurationOpen(false);
                                        setCustomDurationDraft(String(day));
                                        onChange({ indefinite: false, durationDays: day, endDate: null });
                                    }}
                                    sx={chipSx(selected)}
                                />
                            );
                        })}
                        <Chip
                            label={localize("다른 기간")}
                            onClick={() => {
                                setFocusCustomDuration(true);
                                setCustomDurationOpen(prev => !prev);
                                setCustomDurationDraft(String(durationDays || 3));
                            }}
                            sx={chipSx(customDurationOpen)}
                        />
                    </Box>
                    {customDurationOpen && (
                    <Box sx={{ p: 1, borderRadius: 3, bgcolor: '#fafafa', border: '1px solid #eeeeee' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <TextField
                                type="text"
                                autoFocus={focusCustomDuration}
                                value={customDurationDraft}
                                onChange={(event) => {
                                    const nextDraft = normalizeDurationInput(event.target.value);
                                    setCustomDurationDraft(nextDraft);
                                    if (nextDraft) {
                                        onChange({
                                            durationDays: commitDurationDraft(nextDraft),
                                            endDate: null,
                                            indefinite: false,
                                        });
                                    }
                                }}
                                onBlur={() => {
                                    const next = commitDurationDraft(customDurationDraft);
                                    setCustomDurationDraft(String(next));
                                    onChange({ durationDays: next, endDate: null, indefinite: false });
                                }}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                size="small"
                                sx={{ width: 112, ...inputSx }}
                            />
                            <SingleLineText sx={{ fontSize: '0.95rem', color: '#666', fontWeight: 800 }}>{localize("일 동안 복용해요")}</SingleLineText>
                        </Box>
                    </Box>
                    )}
                </>
            )}
        </Box>
    );
}
